import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import Icon from "@icon/Icon";
import ButtonComponent from "@widget/buttons/ButtonComponent";
const STATUS_TEXT = {
    pending: 'Pending',
    running: 'In progress',
    done: 'Done',
    failed: 'Failed',
    skipped: 'Skipped',
};
/**
 * @export
 * A numbered, vertical list of items with a state for each one. Use it for anything that happens to
 * several items in turn: before the work it is the plan, during the work it shows each item's
 * progress, and afterwards it is the result.
 *
 * Each item shows a marker (its number while pending, then a spinner, a check, a cross or a dash),
 * a title, optional detail lines, a status message and an optional action button.
 *
 * @example
 * ```
 * <ProgressList
 *     items={[
 *         { id: 'one', title: 'Building.Dashboard.json', lines: ['Building', 'Level 1'], status: 'done', message: 'Downloaded' },
 *         { id: 'two', title: 'Energy.Dashboard.json', lines: ['Energy'], status: 'running' },
 *         { id: 'three', title: 'Access.Dashboard.json', lines: ['Access'], status: 'pending' },
 *     ]}
 * />
 * ```
 *
 * @example
 * ```
 * <ProgressList
 *     items={[
 *         {
 *             id: 'models',
 *             title: 'Models',
 *             lines: ['Work Request', 'Asset'],
 *             status: 'failed',
 *             message: 'The model Asset already exists.',
 *             action: { label: 'Try again', icon: 'fas rotate-right', onClick: () => retry('models') },
 *         },
 *     ]}
 * />
 * ```
 *
 * @category Display/Status
 */
const ProgressList = ({ items, className }) => {
    const renderMarker = (status, index) => {
        if (status === 'running')
            return _jsx(Icon, { icon: "fas circle-notch", spin: true });
        if (status === 'done')
            return _jsx(Icon, { icon: "fas check" });
        if (status === 'failed')
            return _jsx(Icon, { icon: "fas times" });
        if (status === 'skipped')
            return _jsx(Icon, { icon: "fas minus" });
        return _jsx("span", { children: index + 1 });
    };
    return (_jsx("ol", { className: classNames("uxpcore_progresslist", className), children: (items || []).map((item, index) => {
            const status = item.status || 'pending';
            return (_jsxs("li", { className: classNames("uxpcore_progresslist__item", `uxpcore_progresslist__item--${status}`), "aria-busy": status === 'running', children: [_jsx("div", { className: "uxpcore_progresslist__marker", "aria-label": STATUS_TEXT[status], role: "img", children: renderMarker(status, index) }), _jsxs("div", { className: "uxpcore_progresslist__body", children: [_jsx("div", { className: "uxpcore_progresslist__title", children: item.title }), (item.lines || []).map((line, lineIndex) => (_jsx("div", { className: "uxpcore_progresslist__line", children: line }, lineIndex))), item.message && _jsx("div", { className: "uxpcore_progresslist__message", children: item.message }), item.action && (_jsx("div", { className: "uxpcore_progresslist__action", children: _jsx(ButtonComponent, { title: item.action.label, leftIcon: item.action.icon, variant: "secondary", onClick: item.action.onClick }) }))] })] }, item.id));
        }) }));
};
export default ProgressList;
